import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Container,
  Box,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Header } from '../../components/Header/Header';
import { newServer } from 'constants/constants';
import { useNavigate, useParams } from 'react-router-dom';
import { useLogin } from 'Contexts/AuthProvider/AuthProvider';
import Footer from 'components/Footer/Footer';

interface Plan {
  subscribe_rate_id: string;
  fixed_value: string;
  marketing_title: string;
  marketing_description: string;
  feature: string[];
  days_of_trail_period: number;
  chargeusers: any;
}

const SubscriptionPlans = () => {
  const { user, logout } = useLogin();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const { id, subdomain } = useParams<{ id: string; subdomain: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSubscriptions = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await fetch(`${newServer}subscriptions/all`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await response.json();
        if (result.status === 'JWT_EXPIRED') {
          logout();
          navigate('/enroll');
        }
        setPlans(result.data);
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
      }
    };

    fetchSubscriptions();
  }, []);

  const UserTypesDisplay = ({
    chargeusers,
  }: {
    chargeusers: { web?: number; employee?: number };
  }) => {
    if (!chargeusers) return null;
    const hasWeb = chargeusers.web && chargeusers.web > 0;
    const hasStaff = chargeusers.employee && chargeusers.employee > 0;

    if (!hasWeb && !hasStaff) return null;

    return (
      <Box
        sx={{
          my: 3,
          py: 2,
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {hasWeb && (
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: '0.875rem',
              mb: hasStaff ? 1 : 0,
            }}
          >
            <span style={{ fontWeight: 'bold' }}>{chargeusers.web}</span> Web Users
          </Typography>
        )}
        {hasStaff && (
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: '0.875rem',
            }}
          >
            <span style={{ fontWeight: 'bold' }}>{chargeusers.employee}</span> Staff
          </Typography>
        )}
      </Box>
    );
  };

  const FeatureList = ({ features }: { features: string[] }) => {
    if (!features?.length) return null;

    return (
      <List sx={{ mt: 3 }}>
        {features.map((feature, index) => (
          <ListItem
            key={index}
            sx={{
              py: 1,
              px: 0,
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckCircleOutlineIcon sx={{ color: '#3B82F6', fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary={feature}
              primaryTypographyProps={{
                sx: {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                },
              }}
            />
          </ListItem>
        ))}
      </List>
    );
  };
  const handleSelectSubscription = async (subscribe_rate_id: string) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${newServer}subscriptions/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          subscribe_rate_id,
          submerchant_id: id,
          subdomain,
        }),
      });
      const result = await response.json();
      if (result) {
        const baseUrl = process.env.REACT_APP_PLATFORM_BASE_URL || 'paymylawyer.cc';
        window.location.href = `https://${subdomain}.${baseUrl}/login`;
        logout();
        setSelectedPlan(subscribe_rate_id);
      }
    } catch (error) {
      console.error('Error selecting subscription:', error);
    }
  };

  return (
    <div className='bg-[#111827] '>
      <Header />
      <Container maxWidth='xl' sx={{ py: 6, backgroundColor: '#111827' }}>
        <Typography
          variant='h3'
          component='h2'
          align='center'
          sx={{ mb: 5, fontWeight: 700, color: 'white' }}
        >
          Choose Your Plan
        </Typography>

        <Grid container spacing={3} justifyContent='center'>
          {plans.map((plan) => (
            <Grid
              item
              key={plan.subscribe_rate_id}
              sx={{
                width: '25%',
                minWidth: 280,
                '@media (max-width: 1200px)': {
                  width: '33.33%',
                },
                '@media (max-width: 900px)': {
                  width: '50%',
                },
                '@media (max-width: 600px)': {
                  width: '100%',
                },
              }}
            >
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: 'white',
                  transition: 'all 0.2s ease',
                  borderRadius: 3,
                  position: 'relative',
                  overflow: 'visible',
                  boxShadow:
                    selectedPlan === plan.subscribe_rate_id
                      ? '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                      : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  border: selectedPlan === plan.subscribe_rate_id ? '2px solid #3B82F6' : 'none',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                  },
                }}
                onClick={() => setSelectedPlan(plan.subscribe_rate_id)}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography variant='h5' align='center' sx={{ fontWeight: 700, mb: 2 }}>
                    {plan.marketing_title || 'Subscription Plan'}
                  </Typography>

                  <Box
                    sx={{
                      mb: 3,
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      component='div'
                      sx={{
                        fontWeight: 800,
                        fontSize: '2.5rem',
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        gap: 1,
                      }}
                    >
                      ${parseFloat(plan.fixed_value).toFixed(2)}
                      <Typography
                        component='span'
                        sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
                      >
                        /month
                      </Typography>
                    </Typography>
                  </Box>

                  <UserTypesDisplay chargeusers={plan.chargeusers} />

                  <Button
                    fullWidth
                    size='large'
                    variant={selectedPlan === plan.subscribe_rate_id ? 'contained' : 'outlined'}
                    sx={{
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      mb: 2,
                      bgcolor: selectedPlan === plan.subscribe_rate_id ? '#3B82F6' : 'transparent',
                      borderColor: '#3B82F6',
                      color: selectedPlan === plan.subscribe_rate_id ? 'white' : '#3B82F6',
                      '&:hover': {
                        bgcolor:
                          selectedPlan === plan.subscribe_rate_id
                            ? '#2563EB'
                            : 'rgba(59, 130, 246, 0.04)',
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectSubscription(plan.subscribe_rate_id);
                    }}
                  >
                    {selectedPlan === plan.subscribe_rate_id ? 'Current Plan' : 'Select Plan'}
                  </Button>

                  {plan.marketing_description && (
                    <Typography
                      sx={{
                        textAlign: 'center',
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                        fontWeight: 'bold',
                      }}
                    >
                      {plan.marketing_description}
                    </Typography>
                  )}

                  <FeatureList features={plan.feature} />
                </CardContent>

                <CardActions sx={{ p: 0.5, pt: 0.5, mt: 'auto' }}></CardActions>

                {plan.days_of_trail_period > 0 && (
                  <Typography
                    align='center'
                    sx={{
                      pb: 2,
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                    }}
                  >
                    {plan.days_of_trail_period} Day free trial to start
                  </Typography>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
      <Footer />
    </div>
  );
};

export default SubscriptionPlans;
